import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import QualitySectionCard from './QualitySectionCard';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const QualityInsightsPanel = ({
  loading = false,
  error = null,
  heatmap = null,
  trend = null,
  mttr = null,
  metadata = null,
  batches = null,
}) => {
  const componentTotals = useMemo(() => {
    if (!heatmap?.series) return [];
    return heatmap.series
      .map((series) => {
        const total = series.buckets.reduce((sum, bucket) => sum + Number(bucket.v || 0), 0);
        const latest = series.buckets.slice(-1)[0]?.v ?? 0;
        return {
          label: series.label || 'Unlabeled',
          total,
          latest,
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [heatmap]);

  const recentTrend = useMemo(() => {
    if (!trend?.series) return [];
    return [...trend.series].slice(-6).reverse();
  }, [trend]);

  return (
    <Box sx={{ mt: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={4}>
          <QualitySectionCard
            title="Metadata coverage"
            subtitle="Field completeness across imported bugs"
          >
            {loading ? (
              <Skeleton variant="rounded" height={120} />
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={`Component ${metadata?.pct_with_component ?? 0}%`}
                  color={metadata?.pct_with_component >= 80 ? 'success' : 'warning'}
                  variant="outlined"
                />
                <Chip
                  label={`Description ${metadata?.pct_with_description ?? 0}%`}
                  color={metadata?.pct_with_description >= 80 ? 'success' : 'warning'}
                  variant="outlined"
                />
                <Chip
                  label={`Attachments ${metadata?.pct_with_attachment ?? 0}%`}
                  color={metadata?.pct_with_attachment >= 60 ? 'success' : 'warning'}
                  variant="outlined"
                />
                <Chip
                  label={`Severity ${metadata?.pct_with_severity ?? 0}%`}
                  color={metadata?.pct_with_severity >= 80 ? 'success' : 'warning'}
                  variant="outlined"
                />
                <Chip
                  label={`Assignee ${metadata?.pct_with_assignee ?? 0}%`}
                  color={metadata?.pct_with_assignee >= 80 ? 'success' : 'warning'}
                  variant="outlined"
                />
              </Stack>
            )}
          </QualitySectionCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <QualitySectionCard title="Component density" subtitle="Total and latest week distribution">
            {loading ? (
              <Skeleton variant="rounded" height={180} />
            ) : componentTotals.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No component data yet.
              </Typography>
            ) : (
              <List dense disablePadding>
                {componentTotals.slice(0, 8).map((row) => (
                  <ListItem
                    key={row.label}
                    sx={{
                      px: 0,
                      py: 1.25,
                      borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                      '&:last-of-type': { borderBottom: 'none' },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {row.label}
                        </Typography>
                      }
                    />
                    <Stack direction="row" spacing={3} sx={{ minWidth: 160 }} justifyContent="flex-end">
                      <Box textAlign="right">
                        <Typography variant="caption" color="text.secondary">
                          Total
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {row.total}
                        </Typography>
                      </Box>
                      <Box textAlign="right">
                        <Typography variant="caption" color="text.secondary">
                          Latest
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {row.latest}
                        </Typography>
                      </Box>
                    </Stack>
                  </ListItem>
                ))}
              </List>
            )}
          </QualitySectionCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <QualitySectionCard
            title="Status trend (last 6 weeks)"
            subtitle={trend ? `${formatDate(trend.since)} → ${formatDate(trend.until)}` : 'Awaiting data'}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : recentTrend.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No trend data yet.
              </Typography>
            ) : (
              <Stack spacing={1.25}>
                {recentTrend.map((entry) => (
                  <Stack
                    key={entry.t}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      px: 1,
                      py: 0.75,
                      borderRadius: 2,
                      bgcolor: (theme) =>
                        theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {new Date(entry.t).toLocaleDateString()}
                    </Typography>
                    <Stack direction="row" spacing={3}>
                      <Box textAlign="right">
                        <Typography variant="caption" color="text.secondary">
                          Open
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {entry.open}
                        </Typography>
                      </Box>
                      <Box textAlign="right">
                        <Typography variant="caption" color="text.secondary">
                          Closed
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {entry.closed}
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
          </QualitySectionCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <QualitySectionCard title="Metadata refreshed" subtitle="Latest refresh timestamps">
            {loading ? (
              <Skeleton variant="text" width="80%" />
            ) : (
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>Heatmap:</strong> {heatmap ? `${formatDate(heatmap.since)} → ${formatDate(heatmap.until)}` : '—'}
                </Typography>
                <Typography variant="body2">
                  <strong>Trend window:</strong> {trend ? `${formatDate(trend.since)} → ${formatDate(trend.until)}` : '—'}
                </Typography>
                <Typography variant="body2">
                  <strong>Last refresh:</strong> {metadata ? formatDate(metadata.refreshed_at) : '—'}
                </Typography>
                {metadata?.total_issues != null && (
                  <Typography variant="body2">
                    <strong>Total issues:</strong> {metadata.total_issues}
                  </Typography>
                )}
              </Stack>
            )}
          </QualitySectionCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <QualitySectionCard title="MTTR by component" subtitle="Median and P90 resolution time">
            {loading ? (
              <Skeleton variant="rounded" height={160} />
            ) : mttr?.entries?.length ? (
              <Stack spacing={1.25}>
                {mttr.entries.map((entry) => (
                  <Stack
                    key={entry.component}
                    spacing={1}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {entry.component}
                    </Typography>
                    <Stack direction="row" spacing={3}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          P50 (days)
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {entry.mttr_p50_days != null ? entry.mttr_p50_days.toFixed(2) : '—'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          P90 (days)
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {entry.mttr_p90_days != null ? entry.mttr_p90_days.toFixed(2) : '—'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Resolved
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {entry.resolved_count}
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No MTTR data yet.
              </Typography>
            )}
          </QualitySectionCard>
        </Grid>

        <Grid item xs={12}>
          <QualitySectionCard title="Recent ingestion batches" subtitle="Lineage for Jira imports">
            {loading ? (
              <Skeleton variant="rounded" height={160} />
            ) : batches?.batches?.length ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Batch</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Submitted by</TableCell>
                      <TableCell align="right">Rows</TableCell>
                      <TableCell align="right">Ingested</TableCell>
                      <TableCell align="right">Rejected</TableCell>
                      <TableCell>IP</TableCell>
                      <TableCell>At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batches.batches.map((batch) => (
                      <TableRow key={batch.batch_id} hover>
                        <TableCell>{batch.batch_id}</TableCell>
                        <TableCell>{batch.source}</TableCell>
                        <TableCell>{batch.user || '—'}</TableCell>
                        <TableCell align="right">{batch.rows}</TableCell>
                        <TableCell align="right">{batch.ingested}</TableCell>
                        <TableCell align="right">{batch.rejected}</TableCell>
                        <TableCell>{batch.ip || '—'}</TableCell>
                        <TableCell>{formatDate(batch.at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : error ? (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No batch history available.
              </Typography>
            )}
          </QualitySectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default QualityInsightsPanel;

